import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import {
  SearchPropertyForm,
  SearchForSalePropertiesForm,
  SearchForRentPropertiesForm,
  SearchPFCPropertiesForm,
  SearchPersonByAddressForm,
  SearchPersonForm,
  SearchContactForm,
} from '@/reports/index';
import { StyledHeaderTabsNavigation } from '../../Header/components';
import { TrackEvent, InternalSearchTab, SearchData } from '../interfaces';

interface FormComponentsMap {
  [key: string]: JSX.Element;
}

interface DynamicSearchFormProps {
  internalTabs: InternalSearchTab[];
  defaultForm: string;
  closeForm: () => void;
  placement: 'footer' | 'header' | 'dashboard';
  onSearchSuccess: ((data: SearchData) => void) | undefined;
  onSearchError: ((error: Error) => void) | undefined;
  combinedPropertySearch?: boolean;
}

/**
 * Function used to determine which form component to render based on if the
 * brand has internal tabs for the selected search or not.
 * In case it has internal tabs, we render the tabs navigation component which
 * will allow to switch between the different forms.
 * The form components are mapped and selected based on the libFormComponentName
 * or the defaultForm para value if no internal tabs are present.
 *
 * NOTE: The downside of this approach is that if a brand adds a custom form component,
 * we won't have access to it from here (lib) and won't be able to render it.
 *
 * @param internalTabs - Internal tabs array with label and libFormComponentName
 * @param defaultForm - Default form to render if no internal tabs are present
 * @returns - JSX.Element
 *
 * @example
 * DynamicSearchForm(internalTabs, 'SearchPersonForm')
 * DynamicSearchForm([], 'SearchPersonForm')
 */

export function DynamicSearchForm({
  internalTabs,
  defaultForm,
  closeForm,
  placement,
  onSearchSuccess,
  onSearchError,
  combinedPropertySearch,
}: DynamicSearchFormProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { trackEvent } = useAppConfigContext();

  const handleTabClick =
    (eventCategory: string, customTrackEvent?: TrackEvent) => () => {
      if (customTrackEvent) {
        trackEvent(
          customTrackEvent.category,
          customTrackEvent.action,
          `${placement} ${customTrackEvent.label} ${
            isMobile ? 'mobile' : 'desktop'
          }`
        );
      } else {
        trackEvent(
          `${eventCategory}`,
          'navigation click',
          `${placement} search`
        );
      }
    };

  const formComponentsMap: FormComponentsMap = {
    SearchPersonForm: (
      <SearchPersonForm closeForm={closeForm} placement={placement} />
    ),
    SearchPersonByAddressForm: (
      <SearchPersonByAddressForm closeForm={closeForm} placement={placement} />
    ),
    SearchPropertyForm: (
      <SearchPropertyForm
        onSuccess={onSearchSuccess}
        onError={onSearchError}
        closeForm={closeForm}
        placement={placement}
        combinedPropertySearch={combinedPropertySearch}
      />
    ),
    SearchForSalePropertiesForm: (
      <SearchForSalePropertiesForm
        closeForm={closeForm}
        placement={placement}
      />
    ),
    SearchForRentPropertiesForm: (
      <SearchForRentPropertiesForm
        closeForm={closeForm}
        placement={placement}
      />
    ),
    SearchPFCPropertiesForm: (
      <SearchPFCPropertiesForm closeForm={closeForm} placement={placement} />
    ),
    SearchContactForm: (
      <SearchContactForm
        closeForm={closeForm}
        variant={placement === 'footer' ? 'footer' : undefined}
      />
    ),
  };

  if (internalTabs && internalTabs.length > 0) {
    return (
      <StyledHeaderTabsNavigation
        className={`rf_SearchFormsContainer_internal-tabs ${
          isMobile ? 'mobile' : 'desktop'
        }`}
        tabs={
          internalTabs.map((internalTab) => ({
            label: isMobile ? internalTab.mobileLabel : internalTab.label,
            content: formComponentsMap[internalTab.libFormComponentName],
            trackClick: handleTabClick(
              internalTab.eventCategory,
              internalTab?.customTrackEvent
            ),
          })) || []
        }
      />
    );
  }

  return formComponentsMap[defaultForm] || null;
}
